import React from 'react';
import { Card, CardContent } from '../ui/card';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const num = (v) => new Intl.NumberFormat('pt-BR').format(v || 0);
const pct1 = (v) => `${Math.abs(v).toFixed(1).replace('.', ',')}%`;
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const brlShort = (v) => {
  const a = Math.abs(v || 0);
  const f = (x, d) => x.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: d });
  if (a >= 1e6) return `R$ ${f(v / 1e6, 1)} mi`;
  if (a >= 1e3) return `R$ ${f(v / 1e3, a >= 1e5 ? 0 : 1)} mil`;
  return brl(v);
};

const lyLabel = (mesLabel) => {
  const [m, y] = (mesLabel || '').split('/').map(Number);
  return m && y ? `${MONTHS[m - 1]}/${y - 1}` : 'ano anterior';
};

const Arrow = ({ pct }) => (
  <span className={pct >= 0 ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'}>
    {pct >= 0 ? '▲' : '▼'} {pct1(pct)}
  </span>
);

const Line = ({ children, muted, testId }) => (
  <p className={`text-xs leading-5 h-5 whitespace-nowrap overflow-hidden text-ellipsis ${muted ? 'text-muted-foreground' : ''}`} data-testid={testId}>
    {children}
  </p>
);

const Mini = ({ label, value, title }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">{label}</p>
    <p className="font-mono text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis" title={title || value}>{value}</p>
  </div>
);

const KpiCard = ({ label, value, valueTitle, pago, compare, footer, tvMode, highlight, testId }) => (
  <Card className={`h-full ${highlight ? 'border-primary/30' : ''}`} data-testid={testId}>
    <CardContent className={`h-full flex flex-col ${tvMode ? 'p-8' : 'p-6'}`} style={{ containerType: 'inline-size' }}>
      <p className="text-xs text-muted-foreground uppercase tracking-wider h-4 whitespace-nowrap overflow-hidden text-ellipsis" data-testid={`${testId}-label`}>{label}</p>
      <p className="font-mono font-bold whitespace-nowrap mt-2 leading-tight"
        style={{ fontSize: tvMode ? 'clamp(1.5rem, 9cqi, 3.5rem)' : 'clamp(1.1rem, 9cqi, 2.25rem)' }}
        title={valueTitle} data-testid={`${testId}-value`}>
        {value}
      </p>
      <div className={`mt-1 ${tvMode ? 'text-base' : ''}`}>
        <Line muted testId={`${testId}-pago`}>{pago}</Line>
        <Line testId={`${testId}-compare`}>{compare}</Line>
      </div>
      <div className="mt-auto pt-4">
        <div className="border-t pt-3 grid grid-cols-3 gap-2" data-testid={`${testId}-footer`}>
          {footer.map((f) => <Mini key={f.label} {...f} />)}
        </div>
      </div>
    </CardContent>
  </Card>
);

const NoBase = () => <span className="text-muted-foreground">Sem base de comparação</span>;

const salesFooter = (m) => [
  { label: 'Pedidos', value: num(m?.pedidos) },
  { label: 'Pagos', value: num(m?.pedidos_pagos) },
  { label: 'Ticket', value: brlShort(m?.ticket_medio), title: brl(m?.ticket_medio) },
];

const ComparisonCard = ({ label, m, variation, tvMode, testId }) => (
  <KpiCard
    testId={testId} tvMode={tvMode} label={label}
    value={brl(m?.bruto)}
    pago={<>Pago: {brl(m?.pago)}{variation?.pago !== null && variation?.pago !== undefined && <> (<Arrow pct={variation.pago} />)</>}</>}
    compare={variation?.bruto === null || variation?.bruto === undefined ? <NoBase /> : <><Arrow pct={variation.bruto} /> vendido</>}
    footer={salesFooter(m)}
  />
);

const ProjectionCard = ({ data, tvMode }) => {
  const ok = data && data.available;
  const ly = data?.ano_passado;
  const pagoPct = ok && data.probable ? (data.pago_probable / data.probable) * 100 : null;
  return (
    <KpiCard
      testId="projection-total-card" tvMode={tvMode} label="Projeção do mês"
      value={data === undefined ? '…' : ok ? brl(data.probable) : '—'}
      pago={ok ? <>Pago projetado: {brl(data.pago_probable)}{pagoPct !== null && ` · ${pct1(pagoPct)}`}</> : 'Histórico insuficiente para projeção'}
      compare={ok && ly?.delta_pct !== null && ly?.delta_pct !== undefined
        ? <><Arrow pct={ly.delta_pct} /> vs. {lyLabel(data.mes_label)}</>
        : <span className="text-muted-foreground">Sem comparação com o ano anterior</span>}
      footer={[
        { label: 'Mínimo', value: ok ? brlShort(data.conservative) : '—', title: ok ? brl(data.conservative) : '' },
        { label: 'Máximo', value: ok ? brlShort(data.optimistic) : '—', title: ok ? brl(data.optimistic) : '' },
        { label: 'Projetos', value: data ? `${data.considered} de ${data.total_projects}` : '—' },
      ]}
    />
  );
};

export const KpiCards = ({ totals, period, c1, c2, updatedAt, projTotal, tvMode }) => {
  const p = totals.period || {};
  const updated = updatedAt ? new Date(updatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '--:--';
  const pctPedPagos = p.pedidos ? (p.pedidos_pagos / p.pedidos) * 100 : 0;
  const ly = projTotal?.ano_passado;
  return (
    <div className="space-y-2">
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-4" data-testid="kpi-cards">
        <KpiCard
          testId="sold-period-card" highlight tvMode={tvMode} label={period.card_title}
          value={brl(p.bruto)}
          pago={<>Pago: {brl(p.pago)} · {pct1(p.pct_pago || 0)} do vendido</>}
          compare={<span className="text-muted-foreground">{period.range_label} · atualizado às {updated}</span>}
          footer={salesFooter(p)}
        />
        <ComparisonCard testId="comparison1-card" label={c1.label} m={totals.comparison1} variation={totals.variation1} tvMode={tvMode} />
        {c2 ? (
          <ComparisonCard testId="comparison2-card" label={c2.label} m={totals.comparison2} variation={totals.variation2} tvMode={tvMode} />
        ) : (
          <KpiCard
            testId="period-extra-card" tvMode={tvMode} label="Pedidos e ticket médio"
            value={num(p.pedidos)}
            pago={`Pagos: ${num(p.pedidos_pagos)} pedidos`}
            compare={<span className="text-muted-foreground">Ticket: {brl(p.ticket_medio)} · pago {brl(p.ticket_medio_pago)}</span>}
            footer={[
              { label: 'Pedidos pagos', value: num(p.pedidos_pagos) },
              { label: 'Ticket pago', value: brlShort(p.ticket_medio_pago), title: brl(p.ticket_medio_pago) },
              { label: '% pagos', value: pct1(pctPedPagos) },
            ]}
          />
        )}
        <ProjectionCard data={projTotal} tvMode={tvMode} />
      </div>
      {projTotal?.available && (
        <p className="text-[11px] text-muted-foreground" data-testid="kpi-footnote">
          Projeção: comparação com o ano anterior considera {ly?.projects || 0} {ly?.projects === 1 ? 'projeto' : 'projetos'} com histórico · Projeção estatística baseada no histórico de vendas.
        </p>
      )}
    </div>
  );
};
